import axios from 'axios';
import apiUrls from './apiUrls';
import Cookies from 'js-cookie';

// Helper to get tokens from cookies
const getAuthToken = () => Cookies.get('authToken');
const getRefreshToken = () => Cookies.get('refreshToken');
const getUGXToken = () => Cookies.get('UGXAuthorization');

// Create Axios instance
const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'X-CSRFToken': Cookies.get('csrftoken'),
  },
  timeout: 30000,
  withCredentials: true, // ensures cookies are sent
});

// Request interceptor to set Authorization headers
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const ugxToken = getUGXToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (ugxToken) {
      config.headers.UGXAuthorization = `Uniqgrid ${ugxToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor for 401 and token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const currentPath = window.location.pathname;

    // If already on login page → do nothing
    if (currentPath === '/auth/sign-in') {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const resp = await axios.post(
          apiUrls.refresh,
          { refreshToken },
          { withCredentials: true },
        );

        if (resp.status === 200) {
          Cookies.set('authToken', resp.data.token, {
            secure: true,
            sameSite: 'strict',
          });
          Cookies.set('refreshToken', resp.data.refreshToken, {
            secure: true,
            sameSite: 'strict',
          });

          originalRequest.headers.Authorization = `Bearer ${resp.data.token}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // If refresh fails → logout only if NOT on login page
        Cookies.remove('authToken');
        Cookies.remove('refreshToken');

        if (currentPath !== '/auth/sign-in') {
          window.location.href = '/auth/sign-in';
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
